import React, {useEffect, useMemo, useRef, useState} from 'react';

import {BusinessTransactionChannel} from '@afriex/core/domain/enums';
import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {
  AmountInput,
  Button,
  DatePicker,
  Input,
  Select,
  SpinningLoader,
} from '@/components';
import Modal from '@/components/Modals';
import type {AfriexAdminAmount} from '@/types';
import {readSettlementAccountField} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

import type {InvoiceDraft} from './types';
import {InvoiceActionType} from './types';
import useInvoiceCreate from './useInvoiceCreate';

// The server rejects an invoice whose settlement account is not one of
// these: the snapshot it stores needs an account number and bank name.
const BANK_CHANNELS = new Set([
  BusinessTransactionChannel.BANK_ACCOUNT,
  BusinessTransactionChannel.ACH_BANK_ACCOUNT,
  BusinessTransactionChannel.SWIFT,
  BusinessTransactionChannel.RFP,
]);

/**
 * What an amount fetcher is asked for: the business the invoice bills, and the
 * period it covers. Dates are ISO, omitted when the operator has not picked
 * them yet. `currency` is the one the invoice is being raised in.
 */
export type InvoiceAmountFilter = {
  businessId: string;
  currency?: string;
  fromDate?: string;
  toDate?: string;
};

type InvoiceFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create so the list refreshes in place. */
  onSuccess?: () => void | Promise<void>;
  /**
   * Set when raising an invoice from a merchant's own page: the business is
   * already known, so the field is filled in and locked.
   */
  lockedBusinessId?: string;
  /**
   * Fills the amount for the chosen business and period, which is what the
   * invoice will be raised for. Left out by callers whose amount the operator
   * types; the fee screens pass the overdraft total for the period.
   */
  fetchAmount?: (
    filter: InvoiceAmountFilter,
  ) => Promise<{amount: number; currency: string}>;
  /** Test hook prefix; each rendered element namespaces under it. */
  formDataCy?: string;
};

const InvoiceFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  lockedBusinessId,
  fetchAmount,
  formDataCy,
}: InvoiceFormModalProps) => {
  const {
    invoice,
    dispatch,
    handleSubmit,
    isSubmitting,
    businessName,
    isResolvingBusiness,
    businessNotFound,
    resolveBusiness,
    accounts,
    isLoadingAccounts,
  } = useInvoiceCreate(onClose, onSuccess, lockedBusinessId);

  // The billed period cannot run past today; InvoiceValidator enforces the
  // same rule at submit.
  const today = useMemo(() => new Date(), []);

  const update = (payload: Partial<InvoiceDraft>) =>
    dispatch({type: InvoiceActionType.UPDATE_INVOICE, payload});

  // The fetched total is what the invoice is for, so it is prefilled into the
  // amount (the operator can still adjust it) and shown, which is also how a
  // period with no overdrafts reads as zero rather than as a missing amount.
  // Once the operator has typed an amount it is never overwritten by a fetch.
  const {businessId, periodStart, periodEnd, currency} = invoice;
  const [isFetchingAmount, setIsFetchingAmount] = useState(false);
  // Keyed by the business it was fetched for, so a different business starts
  // afresh: its predecessor's total no longer applies, and an amount typed for
  // that one is not this one's.
  const [fetched, setFetched] = useState<{
    businessId: string;
    amount: number;
    currency: string;
  } | null>(null);
  const fetchedAmount = fetched?.businessId === businessId ? fetched : null;
  const amountEditedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!fetchAmount || !isOpen || !businessId.trim()) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsFetchingAmount(true);
      try {
        const fetchedTotal = await fetchAmount({
          businessId,
          ...(currency ? {currency} : {}),
          ...(periodStart ? {fromDate: periodStart.toISOString()} : {}),
          ...(periodEnd ? {toDate: periodEnd.toISOString()} : {}),
        });
        if (cancelled) {
          return;
        }
        setFetched({...fetchedTotal, businessId});
        if (amountEditedForRef.current !== businessId) {
          dispatch({
            type: InvoiceActionType.UPDATE_INVOICE,
            payload: {amount: fetchedTotal.amount},
          });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        // Best-effort: with nothing fetched the operator types the amount,
        // which is what the form did before a fetcher was passed in.
        setFetched(null);
        logRejection('Unable to fetch the amount for this period')(error);
        showErrorMessage(
          getErrorMessage(error, 'Unable to fetch the amount for this period'),
        );
      } finally {
        if (!cancelled) {
          setIsFetchingAmount(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // `dispatch` is stable; the filters are the inputs that change the answer.
  }, [
    fetchAmount,
    isOpen,
    businessId,
    currency,
    periodStart,
    periodEnd,
    dispatch,
  ]);

  // Only accounts in the chosen currency can be paid into, so the dropdown
  // narrows as soon as a currency is picked. It is also narrowed to bank
  // channels: the server rejects an invoice whose settlement account isn't
  // one, since the invoice snapshot needs an account number and bank name.
  const accountOptions = useMemo(
    () =>
      accounts
        // Saved accounts always carry an id; the predicate narrows it so the
        // option value never falls back to an empty string.
        .filter(
          (account): account is SettlementPaymentMethod & {id: string} =>
            account.id !== undefined &&
            account.currencyCode === invoice.currency &&
            BANK_CHANNELS.has(account.channel),
        )
        .map((account) => ({
          key: `${readSettlementAccountField(account.info, 'accountName')} — ${readSettlementAccountField(account.info, 'bankName')} (${readSettlementAccountField(account.info, 'accountNumber')})`,
          value: account.id,
        })),
    [accounts, invoice.currency],
  );

  // What the fetcher has to say for the current filter, empty-state included:
  // a period with no overdrafts quotes zero rather than looking unfetched.
  const amountHint = (): string => {
    if (isFetchingAmount) {
      return 'Looking up the amount for this period...';
    }
    if (fetchedAmount === null) {
      return 'Pick a merchant and period to fetch the amount.';
    }
    return `${fetchedAmount.currency} ${fetchedAmount.amount.toLocaleString()} for this period.`;
  };

  const businessHint = () => {
    if (!invoice.businessId.trim()) {
      return null;
    }
    if (isResolvingBusiness) {
      return 'Looking up business...';
    }
    if (businessNotFound) {
      return 'No business found for that ID';
    }
    return businessName;
  };

  const hintClass = businessNotFound
    ? 'text-failed-500'
    : 'text-gray-500 dark:text-gray-400';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title="Create Invoice"
      dataCy={formDataCy}
    >
      <div className="pt-3 px-2">
        {isSubmitting ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // handleSubmit toasts its own failures, so this only fires if
              // that handler itself throws.
              handleSubmit(invoice).catch(
                logRejection('Failed to submit invoice'),
              );
            }}
            className="flex flex-col gap-3"
          >
            <Input
              label="Business ID"
              placeholder="Paste the business ID"
              type="text"
              value={invoice.businessId}
              disabled={Boolean(lockedBusinessId)}
              dataCy={formDataCy ? `${formDataCy}-business-id` : undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update({businessId: e.target.value})
              }
              onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                void resolveBusiness(e.target.value)
              }
              className="w-full isFull"
            />
            {businessHint() ? (
              <p className={`mb-2 text-xs ${hintClass}`}>{businessHint()}</p>
            ) : null}

            <Input
              label="Description"
              placeholder="What this invoice covers"
              type="text"
              value={invoice.description}
              dataCy={formDataCy ? `${formDataCy}-description` : undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update({description: e.target.value})
              }
              className="w-full isFull"
            />

            <div className="flex flex-col md:flex-row gap-2">
              {/* react-datepicker only forwards a whitelist of props to its
                  input, so data-cy goes on a wrapper. */}
              <div
                data-cy={formDataCy ? `${formDataCy}-period-start` : undefined}
              >
                <DatePicker
                  label="Period start"
                  selectedDate={invoice.periodStart}
                  setSelectedDate={(date) => update({periodStart: date})}
                  placeholderText="Select date"
                  maxDate={today}
                />
              </div>
              <div
                data-cy={formDataCy ? `${formDataCy}-period-end` : undefined}
              >
                <DatePicker
                  label="Period end"
                  selectedDate={invoice.periodEnd}
                  setSelectedDate={(date) => update({periodEnd: date})}
                  placeholderText="Select date"
                  maxDate={today}
                />
              </div>
            </div>

            {/* Prefilled from the fetched total, never overwritten once edited. */}
            <AmountInput
              label="Amount"
              min={0}
              dataCy={formDataCy ? `${formDataCy}-amount` : undefined}
              value={
                {
                  amount: invoice.amount,
                  currency: invoice.currency,
                } as AfriexAdminAmount
              }
              onChange={(value: AfriexAdminAmount) => {
                if (
                  value.amount !== undefined &&
                  value.amount !== invoice.amount
                ) {
                  amountEditedForRef.current = businessId;
                }
                update({
                  ...(value.amount !== undefined ? {amount: value.amount} : {}),
                  // A new currency invalidates the chosen account, since the
                  // account dropdown only offers accounts in that currency.
                  ...(value.currency && value.currency !== invoice.currency
                    ? {currency: value.currency, settlementAccountId: ''}
                    : {}),
                });
              }}
            />

            {fetchAmount ? (
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                {amountHint()}
              </p>
            ) : null}

            <div data-cy={formDataCy ? `${formDataCy}-due-date` : undefined}>
              <DatePicker
                label="Payment due date"
                selectedDate={invoice.dueDate}
                setSelectedDate={(date) => update({dueDate: date})}
                placeholderText="Select date"
              />
            </div>

            <Select
              label="Settlement account"
              value={invoice.settlementAccountId}
              dataCy={
                formDataCy ? `${formDataCy}-settlement-account` : undefined
              }
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                update({settlementAccountId: e.target.value})
              }
              options={accountOptions}
              disabled={isLoadingAccounts || accountOptions.length === 0}
              className="w-full"
            />
            {!isLoadingAccounts && accountOptions.length === 0 ? (
              <p className="mb-2 text-xs text-failed-500">
                No {invoice.currency} settlement account saved yet. Add one on
                the Settlement Accounts tab.
              </p>
            ) : null}

            <div className="mt-4 flex justify-end gap-3">
              <Button
                onClick={onClose}
                colorScheme="cyan"
                variant="ghost"
                dataCy={formDataCy ? `${formDataCy}-cancel` : undefined}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="cyan"
                disabled={isSubmitting}
                dataCy={formDataCy ? `${formDataCy}-submit` : undefined}
              >
                Create Invoice
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default InvoiceFormModal;
